/**
 * One reading of Merlin's filed brief for a thread, shared by the detail
 * pane and the task-ask modal (kanban 445962e2): which steps are Do next,
 * the chain, the situation tiles, and which desk / step a case-file item
 * restates. Before this the pane computed it inline and the page's task
 * modal did without it, so "Ask Laura" from the case file knew the step and
 * Merlin's draft while "Ask" from the asks rail did not.
 */
import { filedInvestigation, proposedCaseFile } from '../../components/portal/threadTriageView';
import { taskAskDesk, taskAskStep } from '../../components/portal/caseTaskAsks';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type {
  DoorCaseTaskRead,
  DoorThreadRead,
  ThreadCaseFileProposal,
  ThreadInvestigation,
} from '../../services/qcpInvestigations';
import {
  causeChainSteps,
  deskDrafts,
  doNextSteps,
  situationTiles,
  stepForTask,
  type ChainStep,
  type DoNextStep,
  type SituationTile,
} from './merlinBrief';

export interface BriefPlan {
  filing: ThreadInvestigation | null;
  /** The proposed case file, or null once a real case exists (its checklist
   *  then lives on the case file). */
  briefProposal: ThreadCaseFileProposal | null;
  /** Every step as filed, nothing folded: the desk view's input. */
  filedSteps: DoNextStep[];
  filedChain: ChainStep[];
  tiles: SituationTile[];
  /** The Do next list the brief shows: the filed steps under layout D,
   *  else the folded list older filings render. */
  doNext: DoNextStep[];
}

export function briefPlan(
  thread: DoorThreadRead,
  caseFile: Pick<DoorCaseTaskRead, 'id'> | { id: string } | null,
): BriefPlan {
  const filing = filedInvestigation(thread);
  const proposal = proposedCaseFile(thread);
  const briefProposal = caseFile ? null : proposal;
  const filedSteps = filing ? doNextSteps(filing, briefProposal, { fold: false }) : [];
  const filedChain = filing ? causeChainSteps(filing, filedSteps) : [];
  const tiles = filing ? situationTiles(filing, filedChain) : [];
  const doNext = !filing ? [] : tiles.length > 0 ? filedSteps : doNextSteps(filing, briefProposal);
  return { filing, briefProposal, filedSteps, filedChain, tiles, doNext };
}

/**
 * The desk Merlin named on the Do next step each case item restates, by
 * item id: the item's own Ask button names that desk when its label names
 * nobody (QT-10054's "Clear SHERW01 CreditHold Y…" → step 9, Laura Caswell).
 */
export function taskDesksFor(
  tasks: ReadonlyArray<Pick<DoorCaseTaskRead, 'id' | 'label'>>,
  doNext: DoNextStep[],
): Record<string, string> {
  const desks: Record<string, string> = {};
  tasks.forEach((t) => {
    const desk = stepForTask(t.label, doNext)?.desk;
    if (desk) desks[t.id] = desk;
  });
  return desks;
}

export interface TaskAskPlan {
  /** The desk the modal opens for ('' when nobody is named). */
  desk: string;
  /** The Do next step the item restates, when the brief has one. */
  stepNo: number | null;
  /** What the request quotes: the item without its desk. */
  steps: string[];
  /** Merlin's unsent drafts for that step — the modal opens on them. */
  drafts: DoorDataRequestRead[];
}

/**
 * How "Ask <desk>" on a case item opens, wherever the button sits: the
 * desk, the step it restates, and Merlin's pre-written request for that
 * step. With no thread (a case saved from chat) there is no brief to match
 * against, so the item's own label is all there is.
 */
export function taskAskPlan(
  task: DoorCaseTaskRead,
  thread: Pick<DoorThreadRead, 'id'> | null,
  doNext: DoNextStep[],
  asks: ReadonlyArray<DoorDataRequestRead>,
): TaskAskPlan {
  const step = stepForTask(task.label, doNext);
  const desk = taskAskDesk(task, step?.desk).desk;
  const stepNo = step?.no ?? null;
  const drafts =
    stepNo != null && thread ? deskDrafts({ steps: [{ no: stepNo }] }, thread, [...asks]) : [];
  return { desk, stepNo, steps: [taskAskStep(task)], drafts };
}
